/**
 * Meta signup team-size bands (the ICP granularity) + the value we attach to a
 * qualified-signup conversion.
 *
 * This module is deliberately PURE and directive-free (anti-pattern #18): it is
 * imported by BOTH the client signup wizard (to value the browser Pixel fire)
 * and the server CAPI sender (src/lib/meta/capi.ts). It must never import a
 * node-only API or carry a 'use client' / 'use server' directive, or one side
 * or the other breaks at build/render time with nothing the compiler can see.
 */
import { parseDeclaredTeamSize, type TeamSizeBucket } from '@/lib/accounts/team-size';

/**
 * The bands the signup team-size step offers. Finer than accounts.team_size's
 * solo/2-10/10-20/20+ on purpose: the ICP is 5–30 people, and '2-10' straddles
 * that line. NON-OVERLAPPING by design — every head count falls in exactly one
 * band (solo=1, then 2-5, 6-10, 11-20, 21+), so nobody can pick two.
 */
export const SIGNUP_TEAM_BANDS = ['solo', '2-5', '6-10', '11-20', '21+'] as const;
export type SignupTeamBand = (typeof SIGNUP_TEAM_BANDS)[number];

export function isSignupTeamBand(v: unknown): v is SignupTeamBand {
  return typeof v === 'string' && (SIGNUP_TEAM_BANDS as readonly string[]).includes(v);
}

/**
 * Proxy expected-value per band, in GBP pence, attached to the
 * CompleteRegistration conversion so Meta optimises toward ICP-shaped signups.
 * Three tiers: solo (low) < 2-5 (medium) < 6+ (high). The ABSOLUTE numbers are
 * tunable — it is the RELATIVE order that drives value optimisation — but they
 * are set to a rough expected first-year worth so a blended value reads sensibly.
 */
const BAND_VALUE_PENCE: Record<SignupTeamBand, number> = {
  solo: 500, //    low  — captured but deprioritised
  '2-5': 2500, //  medium
  '6-10': 9000, // high — core ICP (5–30)
  '11-20': 9000, // high
  '21+': 9000, //  high
};

export function bandValuePence(band: SignupTeamBand): number {
  return BAND_VALUE_PENCE[band];
}

/**
 * Map a fine signup band onto the canonical accounts.team_size bucket, so
 * onboarding / scoring readers see a size at signup. Reuses parseDeclaredTeamSize
 * — the one place that coerces size text into a bucket — rather than a second
 * lookup table that could drift from it. ('2-5'→'2-10', '6-10'→'2-10',
 * '11-20'→'10-20', '21+'→'20+', 'solo'→'solo'.)
 */
export function bandToCanonicalTeamSize(band: string): TeamSizeBucket | null {
  return parseDeclaredTeamSize(band);
}
